import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PenLine, CheckCircle2, FileText, Eraser } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function NominationSignPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [saving, setSaving] = useState(false);
  const [signed, setSigned] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const emptyRef = useRef(true);

  useEffect(() => {
    axios.get(`${BACKEND}/api/nominations/public/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data.role_signed) setSigned(true);
      })
      .catch((err) => setError(err.response?.data?.detail || "Link non valido"));
  }, [token]);

  // Canvas drawing setup
  useEffect(() => {
    if (!canvasRef.current || signed || !data) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    // High-DPI setup
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = "#0f172a";
    // Clear
    ctx.clearRect(0, 0, rect.width, rect.height);
    emptyRef.current = true;

    const getPos = (e) => {
      const r = canvas.getBoundingClientRect();
      const clientX = e.touches?.[0]?.clientX ?? e.clientX;
      const clientY = e.touches?.[0]?.clientY ?? e.clientY;
      return { x: clientX - r.left, y: clientY - r.top };
    };
    const start = (e) => {
      e.preventDefault();
      drawingRef.current = true;
      const p = getPos(e);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    };
    const move = (e) => {
      if (!drawingRef.current) return;
      e.preventDefault();
      const p = getPos(e);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      emptyRef.current = false;
    };
    const end = () => { drawingRef.current = false; };

    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mousemove", move);
    canvas.addEventListener("mouseup", end);
    canvas.addEventListener("mouseleave", end);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", end);
    return () => {
      canvas.removeEventListener("mousedown", start);
      canvas.removeEventListener("mousemove", move);
      canvas.removeEventListener("mouseup", end);
      canvas.removeEventListener("mouseleave", end);
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", end);
    };
  }, [data, signed]);

  const clear = () => {
    if (!canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    emptyRef.current = true;
  };

  const submit = async () => {
    if (!signerName.trim() || signerName.trim().length < 2) return toast.error("Inserisci il tuo nome");
    if (emptyRef.current) return toast.error("Disegna la tua firma nel riquadro");
    setSaving(true);
    try {
      const png = canvasRef.current.toDataURL("image/png");
      const r = await axios.post(`${BACKEND}/api/nominations/public/${token}/sign`, {
        signer_name: signerName.trim(),
        signature_png_base64: png,
      });
      toast.success("Firma acquisita — grazie!");
      setSigned(true);
      // Reload state to show signed pdf
      const rr = await axios.get(`${BACKEND}/api/nominations/public/${token}`);
      setData(rr.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore firma");
    }
    setSaving(false);
  };

  if (error) return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="bg-white border border-border rounded-lg p-8 max-w-md text-center">
        <FileText className="w-10 h-10 mx-auto text-rose-500 mb-2" />
        <h1 className="text-xl font-bold">Link non valido</h1>
        <p className="text-sm text-muted-foreground mt-1">{error}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen grid place-items-center text-muted-foreground">Caricamento…</div>;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="nomination-sign-page">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="bg-white border border-border rounded-lg p-6">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Firma elettronica</div>
          <h1 className="text-2xl font-bold mt-1">{data.template_label}</h1>
          {data.company_name && <p className="text-sm text-muted-foreground mt-1">Emessa da <b>{data.company_name}</b></p>}
          {data.role_label && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20" data-testid="nomination-role-badge">
              Stai firmando come: {data.role_label}
            </div>
          )}
          {data.other_signed && !data.fully_signed && (
            <p className="text-xs text-emerald-700 mt-2">✓ L'altra parte ha già firmato — manca solo la tua firma.</p>
          )}
          {data.role_signed && !data.fully_signed && (
            <p className="text-xs text-amber-700 mt-2">In attesa della firma dell'altra parte per completare il documento.</p>
          )}
        </div>

        <div className="bg-white border border-border rounded-lg p-6 whitespace-pre-wrap text-sm leading-relaxed" data-testid="nomination-sign-body">
          {data.body}
        </div>

        {signed || data.role_signed ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center" data-testid="nomination-signed">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
            <h2 className="text-xl font-bold text-emerald-900">
              {data.fully_signed ? "Documento firmato da entrambe le parti" : "La tua firma è stata acquisita"}
            </h2>
            <p className="text-sm text-emerald-800 mt-1">
              {data.fully_signed
                ? "Grazie. Il documento è ora archiviato con entrambe le firme."
                : "Grazie. Attendiamo la firma dell'altra parte per completare l'archiviazione."}
            </p>
            {data.signed_pdf_url && (
              <a href={`${BACKEND}${data.signed_pdf_url}`} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 mt-3 text-sm text-primary hover:underline">
                <FileText className="w-4 h-4" /> Scarica il PDF firmato
              </a>
            )}
          </div>
        ) : (
          <div className="bg-white border border-border rounded-lg p-6 space-y-4">
            <h2 className="font-semibold flex items-center gap-2"><PenLine className="w-4 h-4" /> Firma qui</h2>
            <div>
              <Label>Nome e cognome</Label>
              <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Es. Mario Rossi" data-testid="nomination-signer-name" />
            </div>
            <div>
              <Label>Firma</Label>
              <canvas ref={canvasRef} className="w-full border-2 border-dashed border-border rounded-md bg-white cursor-crosshair touch-none" style={{ height: 200 }} data-testid="nomination-signature-canvas" />
              <div className="flex justify-between items-center mt-2">
                <button type="button" onClick={clear} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="nomination-clear">
                  <Eraser className="w-3 h-3" /> Cancella
                </button>
                <span className="text-[10px] text-muted-foreground">Disegna con dito o mouse</span>
              </div>
            </div>
            <Button onClick={submit} disabled={saving} className="w-full rounded-full btn-lift" data-testid="nomination-submit-sign">
              {saving ? "Invio firma…" : "Firma e conferma"}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">
              Cliccando "Firma e conferma" accetti che nome, timestamp e indirizzo IP vengano archiviati insieme alla firma a fini probatori.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
